
import {Utils} from '../common/Utils';
import {BaseModel} from "./BaseModel";

/**
 * A UserModel represents an entry from the ARGO collection "user". It handles all CRUD (create, read, update, delete)
 * methods and parsing JSON from the backend to a readable model. Keep in mind that most of the update actions
 * requires ADMIN privileges.
 *
 */
export class UserModel extends BaseModel {

  /**
   *
   * @param {Object} options - configuration options
   */
  constructor(options = {}) {

    // for this model only "users" collection will be supported
    options.collection = 'users';

    // copy data and prevent parsing by BaseClass
    let data = options.data;
    delete options.data;

    super(options);

    /**
     * user's personal data
     * @type {Object}
     * @private
     */
    this._personalData = {};
    /**
     * user's settings
     * @type {Object}
     * @private
     */
    this._settings = {};

    // parse preset data.
    if (data) {
      this.parse(data, {silent: true});
    }
  }

  /**
   * Return all necessary data from the user.
   * @return {Object}
   */
  getData() {
    let data = super.getData();
    data.personalData = this._personalData;
    data.settings = this.getSetting();
    return data;
  }

  /**
   * Returns the data object that will be used to sent to the backend.
   * @return {Object}
   * @private
   */
  _getRequestData(props = this.properties) {
    return {
      /*...props,*/
      personalData: this._personalData,
      settings: this._settings,
      properties: props
    };
  }

  /**
   * Checks if the model has a personal data property with given key and the value
   * is not undefined nor null.
   * @param {String} key - personal data property name
   * @return {boolean}
   */
  hasPersonalData(key) {
    return !Utils.isUndefOrNull(this._personalData[key]);
  }

  /**
   * Returns the value from user's personal data with given key or
   * all properties if key is not set.
   * @param {String} [key] - name of the personal data property name
   * @return {*}
   */
  getPersonalData(key) {
    if (key) {
      return this._personalData.hasOwnProperty(key) ? this._personalData[key] : null;
    }
    return this._personalData;
  }

  /**
   * Set user's personal data with given key and value.
   * Key can be also an key-value object.
   * @param {String|Object} key - setting name or key-value object
   * @param {*} [value] - value to be set
   * @return {UserModel}
   */
  setPersonalData(key, value) {

    // transform key-value call to object
    if (Utils.isString(key)) {
      key = {[key]: value};
    }

    for (const pKey in key) {
      this._personalData[pKey] = key[pKey];
    }

    return this;
  }

  /**
   * Checks the model has a setting property with given key and the value
   * is not undefined nor null.
   * @param {String} key - setting property name
   * @return {boolean}
   */
  hasSetting(key) {
    return !Utils.isUndefOrNull(this._settings[key]);
  }

  /**
   * Returns the value from user's settings data with given key or
   * all settings if key is not set.
   * Value of a setting will be parsed (JSON) if possible.
   * @param {String} [key] - name of the settings data property name
   * @return {*}
   */
  getSetting(key) {
    if (key) {
      if (!this._settings.hasOwnProperty(key)){
        return null;
      }
      let v = this._settings[key];
      if (!Utils.isString(v)) {
        return v;
      }
      // String could be a JSON stringified object
      if (v.startsWith('[') && v.endsWith(']') || v.startsWith('{') && v.endsWith('}')) {
        try {
          v = JSON.parse(v);
        } catch(e) {}
      }
      return v;
    }

    let map = {};
    for (let k in this._settings) {
      map[k] = this.getSetting(k);
    }
    return map;
  }

  /**
   * Set user's setting with given key and value.
   * Value will be stringified if value is an array or object.
   * @param {String|Object} key - setting name or key-value object
   * @param {*} [value] - value to be set
   * @return {UserModel}
   */
  setSetting(key, value) {

    // transform key-value call to object
    if (Utils.isString(key)) {
      key = {[key]: value};
    }

    for (const pKey in key) {
      let v = key[pKey];
      if (!Utils.isSimpleType(v)) {
        v = JSON.stringify(v);
      }
      this._settings[pKey] = v;
    }

    return this;
  }

  /**
   * Extract personal data and settings from the response.
   * @param {Object} data - response from the ARGO API
   * @param {Object} options - options passed to origin parse function
   */
  onBeforeParse(data, options = {}) {

    // extract personalData properties
    if (data.personalData) {
      this.setPersonalData(data.personalData);
      delete data.personalData;
    }

    // extract personalData properties
    if (data.settings) {
      this.setSetting(data.settings);
      delete data.settings;
    }
  }

}